import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { Category, CloudUpload, Polyline } from "@mui/icons-material";
import { Box, Button, FormHelperText, LinearProgress, Paper, Stack, Typography } from "@mui/material";

import { Alert } from "fond/project/panels/InputDataPanel.styles";
import ImportDataProgress from "fond/project/panels/polygon/ImportDataProgress";
import PolygonSelectBody from "fond/project/panels/polygon/PolygonSelectBody";
import { useAreaSelect } from "fond/project/panels/polygon/useAreaSelect";
import { usePolygonSelect } from "fond/project/panels/polygon/usePolygonSelect";
import { PolygonState } from "fond/project/polygon/redux";
import { closePolygonSelect } from "fond/project/redux";
import { Store } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";

import { BASE_DATA_LAYERS, BaseDataLayerKey } from "../layers";

import { FlyoutSubOption, FlyoutSubOptions } from "./FlyoutSubOptions";
import { getBaseDataLayerGroupId } from "./layerGroups";
import MapModeBanner from "./MapModeBanner";
import ReplaceDataConfirmation from "./ReplaceDataConfirmation";
import { useImportWithinBoundary } from "./useImportWithinBoundary";

interface AreaSelectSectionProps {
  layerKey: BaseDataLayerKey;
  /** Called once the selected data is imported, or the user backs out of replacing existing data. */
  onComplete(): void;
}

/**
 * The expanded Area select option: import the FOND data inside the boundary, or inside an area drawn or uploaded.
 */
const AreaSelectSection: React.FC<AreaSelectSectionProps> = ({ layerKey, onComplete }) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const layerGroupId = getBaseDataLayerGroupId(layerKey) ?? "";
  const { hasData, uploadedArea, beginPolygonSelect, selectNewPolygon, selectPolygonUpload, close } = useAreaSelect(layerGroupId);
  const polygonSelect = usePolygonSelect();
  const { polygon, activeLayerGroupId, importSelectedArea } = polygonSelect;
  const boundaryImport = useImportWithinBoundary(layerKey, onComplete);
  const [isReplaceConfirmed, setIsReplaceConfirmed] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const isActive = polygon.isOpen && activeLayerGroupId === layerGroupId;
  const showImportProgress = isImporting || (isActive && polygon.polygonState === PolygonState.upload);
  const isDrawing = isActive && (polygon.polygonState === PolygonState.nullDrawing || polygon.polygonState === PolygonState.partial);

  // Leave polygon select when the section closes, unless its data is being imported.
  useEffect(
    () => () => {
      const { project } = store.getState();
      if (project.polygon.isOpen && project.activeLayerGroupId === layerGroupId && project.polygon.polygonState !== PolygonState.upload) {
        dispatch(closePolygonSelect());
      }
    },
    [dispatch, layerGroupId, store]
  );

  const startAreaSelect = (selectArea: () => void) => {
    beginPolygonSelect();
    selectArea();
  };

  const handleImport = () => {
    importSelectedArea();
    setIsImporting(true);
  };

  if (!layerGroupId) return null;

  if (boundaryImport.status === "downloading" || boundaryImport.status === "uploading") {
    return (
      <Paper variant="outlined" sx={{ py: 2, px: 2.75 }}>
        <Typography variant="body1" fontWeight={500}>
          In progress
        </Typography>
        <LinearProgress />
        <FormHelperText color="primary">
          {boundaryImport.status === "downloading" ? "Downloading FOND data inside the boundary" : "Uploading data"}
        </FormHelperText>
      </Paper>
    );
  }

  if (boundaryImport.status === "error") {
    return (
      <Alert severity="error">
        <Typography variant="body3" component="p" mb={1}>
          {boundaryImport.error}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={boundaryImport.reset}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={boundaryImport.importWithinBoundary}>
            Retry
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (showImportProgress) {
    return (
      <ImportDataProgress
        onSuccess={() => {
          setIsImporting(false);
          onComplete();
        }}
        onCancel={() => setIsImporting(false)}
      />
    );
  }

  if (isActive) {
    return (
      <Box>
        {isDrawing && (
          <MapModeBanner
            title={`Drawing area for ${BASE_DATA_LAYERS[layerKey].name.toLowerCase()}`}
            hint="Click to add points. Double-click to finish."
          >
            <Button size="small" onClick={close}>
              Cancel
            </Button>
          </MapModeBanner>
        )}
        <PolygonSelectBody polygonSelect={polygonSelect} onImport={handleImport} onCancel={close} />
        {polygon.polygonState !== PolygonState.complete && (
          <Box display="flex" justifyContent="flex-end" px={1} pt={1}>
            <Button size="small" onClick={close}>
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  if (hasData && !isReplaceConfirmed) {
    return (
      <ReplaceDataConfirmation
        message="You have already added data for this layer. Proceeding with Area Select will replace it."
        onCancel={onComplete}
        onReplace={() => setIsReplaceConfirmed(true)}
      />
    );
  }

  return (
    <FlyoutSubOptions>
      <FlyoutSubOption
        icon={<Category />}
        label="Use boundary"
        description={uploadedArea ? "Reuse the existing boundary" : "Add a boundary first"}
        disabled={!uploadedArea}
        tooltip={uploadedArea ? undefined : "Add a boundary first"}
        onClick={boundaryImport.importWithinBoundary}
      />
      <FlyoutSubOption
        icon={<Polyline />}
        label="Draw area"
        description="Draw a polygon on the map"
        onClick={() => startAreaSelect(selectNewPolygon)}
      />
      <FlyoutSubOption
        icon={<CloudUpload />}
        label="Upload area"
        description="Upload a polygon"
        onClick={() => startAreaSelect(selectPolygonUpload)}
      />
    </FlyoutSubOptions>
  );
};

export default AreaSelectSection;
